import { uiEffectsEnabled } from "../../graphics/graphics_quality.js";
import { Vector2 } from "../../common/vector2.js";
import { Rect } from "../../common/rect.js";
import { Transformable } from "../../transforms/transformable.js";
import { Easing, TransformBase } from "../../transforms/transform_base.js";
import { ColorScheme } from "./color_scheme.js";
import { isNullCanvasContext } from "../dom_backend/null_canvas_context.js";
import { getDomHost, DOM_ANIMATIONS_SUPPORTED, DOM_INDIVIDUAL_TRANSFORMS, domScaleCss, isDomScaleCollapsed, pauseForHiddenHost, resumeDomAnimation, snapToDevicePx } from "../dom_backend/dom_backend.js";
import { DomTranslateOrigin, domPredecessorDoneBy, releaseDomAnimation, startDomAnimation } from "../dom_backend/dom_animations.js";

let dom_set_context_warned = false;

// A darkened copy depends only on (source image, darkness factor), and the same
// icon is darkened by many elements (star and reward on every claimed daily
// task card). Building it on every render allocated a canvas and did a GPU
// readback (getImageData) per element per frame: 15-90 ms frames while a
// claimed card was on screen. Keyed weakly so unloaded images can be dropped.
const darkened_cache = new WeakMap<object, Map<number, HTMLCanvasElement>>();

// True while an image somewhere in the subtree has not loaded yet (it draws
// nothing). Bitmap caches of a subtree use it to know they must be rebuilt.
export function subtreeHasUnloadedImages( element: UIElement ): boolean
{
  const with_image = element as unknown as { getImage?: () => { width: number } | null | undefined };
  if ( typeof with_image.getImage === "function" )
  {
    const image = with_image.getImage();
    if ( !image || image.width <= 1 )
      return true;
  }

  for ( const child of element.getChildren() )
  {
    if ( subtreeHasUnloadedImages( child ) )
      return true;
  }
  return false;
}

export type TransformOptions = {
  direction_x?: number;
  direction_y?: number;
  rotation_angle?: number;
  image_opacity?: number;
  darkness_factor?: number;
  pressed_scale?: number;
};

export abstract class UIElement extends Transformable
{
  private static DEFAULT_PRESSED_SCALE: number = 0.95;
  private static PRESS_DURATION: number = 40;
  private static RELEASE_DURATION: number = 40;

  protected ctx: CanvasRenderingContext2D;
  protected rect: Rect;
  private children: UIElement[] = [];
  private onClick?: ( p: Vector2 ) => void;
  private replay_click_on_root = false;
  protected is_pressed: boolean = false;

  private pressed_scale: number;

  private is_part_of_parent_not_a_separate_element: boolean = false;
  private is_button: boolean = false;
  private is_clickable: boolean = true;

  private static onAnyTouchCallback?: ( element: UIElement ) => void;

  protected rotatable: boolean = false;
  protected direction_x: number = 1;
  protected direction_y: number = 1;
  protected rotation_angle: number = 0;
  protected image_opacity: number = 1.0;
  protected darkness_factor: number = 0.0;

  private was_pressed_inside: boolean = false;

  protected readonly dom_mode: boolean;
  private dom_node: HTMLDivElement | null = null;
  private dom_host: HTMLDivElement | null = null;
  private dom_backdrop: HTMLDivElement | null = null;
  private dom_origin_x = 0;
  private dom_origin_y = 0;
  private dom_last_backdrop: string = "";
  private dom_island: HTMLCanvasElement | null = null;
  private dom_island_ctx: CanvasRenderingContext2D | null = null;
  private dom_null_ctx: CanvasRenderingContext2D | null = null;
  private dom_last_size: string = "";
  private dom_last_translate: string = "";
  private dom_last_rotate: string = "";
  private dom_last_scale: string = "";
  private dom_last_static_transform: string = "";
  private dom_last_transform: string = "";
  private dom_last_opacity: string = "";
  private dom_last_visibility: string = "";
  private dom_last_native: boolean = true;
  private dom_hidden: boolean = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    is_part_of_parent_not_a_separate_element: boolean = false,
    is_scalable_on_click = false,
    options?: TransformOptions
  )
  {
    super( new Vector2( x, y ) );

    this.ctx = ctx;
    this.dom_mode = isNullCanvasContext( ctx );
    this.dirty_on_set = this.dom_mode;
    this.rect = new Rect( 0, 0, width, height );

    this.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;
    this.is_button = is_scalable_on_click;
    this.pressed_scale = options?.pressed_scale ?? UIElement.DEFAULT_PRESSED_SCALE;
    this.applyTransformOptions(options);
  }
  protected applyTransformOptions(options?: TransformOptions): void {
    if (options?.direction_x !== undefined && options?.direction_y !== undefined) {
      this.scaleElement(options.direction_x, options.direction_y);
    }

    if (options?.rotation_angle !== undefined) {
      this.setRotationDegrees(options.rotation_angle);
    }

    if (options?.image_opacity !== undefined) {
      this.setImageOpacity(options.image_opacity);
    }

    if (options?.darkness_factor !== undefined) {
      this.setDarknessFactor(options.darkness_factor);
    }
  }

  protected scaleElement(x: number, y: number): void {
    this.direction_x = x;
    this.direction_y = y;
  }

  protected setRotationDegrees(angle_in_degrees: number): void {
    this.rotation_angle = angle_in_degrees * Math.PI / 180;
  }

  public setImageOpacity(opacity: number): void {
    this.image_opacity = opacity;
  }

  protected setDarknessFactor(factor: number): void {
    this.darkness_factor = Math.max( 0, Math.min( 1, factor ) );
  }

  public getDarknessFactor(): number {
    return this.darkness_factor;
  }

  public setClickable( value: boolean ): void
  {
    this.is_clickable = value;
  }

  public getContext(): CanvasRenderingContext2D
  {
    return this.ctx;
  }

  public getAbsolutePosition(): Vector2 {
    let absoluteX = this.pixelPosForRender().x;
    let absoluteY = this.pixelPosForRender().y;

    let current = this.getParentElement();
    while (current) {
      const parentPos = current.pixelPosForRender();
      absoluteX += parentPos.x;
      absoluteY += parentPos.y;
      current = current.getParentElement();
    }

    return new Vector2(absoluteX, absoluteY);
  }

  public getActualOpacity(): number
  {
    let opacity = this.getOpacityForRender();
    let current = this.getParentElement();
    while ( current !== null )
    {
      opacity *= current.getOpacityForRender();
      current = current.getParentElement();
    }
    return opacity;
  }

  public getAbsoluteLayoutForHtml(): { x: number; y: number; scale_x: number; scale_y: number }
  {
    const chain: UIElement[] = [];
    let curr: UIElement | null = this;
    while ( curr !== null )
    {
      chain.unshift( curr );
      curr = curr.getParentElement();
    }

    let canvas_x = 0;
    let canvas_y = 0;
    let cum_sx   = 1;
    let cum_sy   = 1;

    for ( let i = 0; i < chain.length - 1; i++ )
    {
      const elem = chain[ i ];
      const s    = elem.getScaleForRender();
      const px   = elem.pixelPosForRender().x;
      const py   = elem.pixelPosForRender().y;
      const w2   = elem.rect.getW2();
      const h2   = elem.rect.getH2();

      canvas_x += cum_sx * ( px + w2 * ( 1 - s.x ) );
      canvas_y += cum_sy * ( py + h2 * ( 1 - s.y ) );

      cum_sx *= s.x;
      cum_sy *= s.y;
    }

    canvas_x += cum_sx * this.pixelPosForRender().x;
    canvas_y += cum_sy * this.pixelPosForRender().y;

    return { x: canvas_x, y: canvas_y, scale_x: cum_sx, scale_y: cum_sy };
  }

  public getAbsoluteRect(): { x: number, y: number, width: number, height: number } {
    const absPos = this.getAbsolutePosition();
    return {
      x: absPos.x,
      y: absPos.y,
      width: this.rect.getW(),
      height: this.rect.getH()
    };
  }

  private parent_element: UIElement | null = null;

  public getParentElement(): UIElement | null {
    return this.parent_element;
  }

  public setParentElement(parent: UIElement | null): void {
    this.parent_element = parent;
  }

  protected createDarkenedImage( image: ImageBitmap ): HTMLCanvasElement
  {
    const factor = this.darkness_factor;
    const cached = darkened_cache.get( image )?.get( factor );
    if ( cached )
      return cached;

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = ( image as any ).width;
    off_canvas.height = ( image as any ).height;
    const off_ctx = off_canvas.getContext( "2d" )!;

    // Scale the colour by (1 - factor) and keep alpha: black drawn source-atop
    // at alpha = factor. Same result as the old per-pixel loop, without the
    // getImageData GPU readback that took 40+ ms per icon on device.
    off_ctx.drawImage( image, 0, 0 );
    off_ctx.globalCompositeOperation = "source-atop";
    off_ctx.fillStyle = `rgba(0, 0, 0, ${Math.max( 0, Math.min( 1, factor ) )})`;
    off_ctx.fillRect( 0, 0, off_canvas.width, off_canvas.height );
    off_ctx.globalCompositeOperation = "source-over";

    // Placeholder bitmaps (<= 1 px) get replaced once the real image loads;
    // only real images are worth keeping.
    if ( off_canvas.width > 1 && off_canvas.height > 1 )
    {
      let by_factor = darkened_cache.get( image );
      if ( !by_factor )
      {
        by_factor = new Map();
        darkened_cache.set( image, by_factor );
      }
      by_factor.set( factor, off_canvas );
    }
    return off_canvas;
  }

  protected applyTransform(): void {
    this.ctx.scale(this.direction_x, this.direction_y);
    this.ctx.rotate(this.rotation_angle);
    this.ctx.globalAlpha *= this.image_opacity;
  }

  public enableRotation()
  {
    this.rotatable = true;
  }

  public isRotatable(): boolean
  {
    return this.rotatable;
  }

  public removeAllChildren(): void
  {
    if ( this.dom_mode )
    {
      for ( const child of this.children )
      {
        child.releaseDomAnimations( true );
        child.dom_node?.remove();
      }
    }
    this.children = [];
  }

  private releaseDomAnimations( recurse: boolean ): void
  {
    this.detachDomTransforms();
    if ( recurse )
    {
      for ( const child of this.children )
        child.releaseDomAnimations( true );
    }
    const node = this.dom_node;
    if ( node && typeof node.getAnimations === "function" )
    {
      for ( const anim of node.getAnimations( { subtree: true } ) )
        anim.cancel();
    }
  }


  public isDomMode(): boolean { return this.dom_mode; }

  public getDomNode(): HTMLDivElement
  {
    if ( !this.dom_node )
    {
      const node = document.createElement( "div" );
      node.className = "ui-dom-el";
      node.dataset.ui = this.name();
      const s = node.style;
      s.position = "absolute";
      s.left = "0";
      s.top = "0";
      s.transformOrigin = "50% 50%";
      s.pointerEvents = "none";
      this.dom_node = node;
    }
    return this.dom_node;
  }

  public getDomHost(): HTMLDivElement | null
  {
    return this.dom_host;
  }

  private dom_skin: HTMLDivElement | null = null;

  protected getDomSkin(): HTMLDivElement
  {
    if ( !this.dom_skin )
    {
      const skin = document.createElement( "div" );
      skin.className = "ui-dom-skin";
      const s = skin.style;
      s.position = "absolute";
      s.left = "0";
      s.top = "0";
      s.width = "100%";
      s.height = "100%";
      s.pointerEvents = "none";
      const node = this.getDomNode();
      node.insertBefore( skin, node.firstChild );
      this.dom_skin = skin;
    }
    return this.dom_skin;
  }

  public mountDomHost( canvas: HTMLCanvasElement ): void
  {
    if ( !this.dom_mode )
      return;

    const host = getDomHost( canvas );
    const node = this.getDomNode();
    if ( this.dom_host === host && node.parentNode === host )
      return;

    this.dom_host = host;
    if ( this.dom_backdrop )
      host.insertBefore( this.dom_backdrop, host.firstChild );
    host.appendChild( node );
    this.invalidateSubtree();
  }

  public setDomHidden( hidden: boolean, redraw: boolean = true ): void
  {
    if ( !this.dom_mode )
      return;

    const node = this.getDomNode();
    const visibility = hidden ? "hidden" : "";
    this.dom_hidden = hidden;
    node.style.visibility = visibility;
    const display = hidden && !uiEffectsEnabled() ? "none" : "";
    if ( node.style.display !== display )
      node.style.display = display;
    if ( this.dom_backdrop )
    {
      this.dom_backdrop.style.visibility = visibility;
      this.dom_backdrop.style.display = display;
    }

    if ( typeof node.getAnimations === "function" )
    {
      for ( const anim of node.getAnimations( { subtree: true } ) )
      {
        if ( hidden && anim.playState === "running" )
          anim.pause();
        else if ( !hidden && anim.playState === "paused" )
          resumeDomAnimation( anim );
      }
    }

    if ( !hidden && redraw )
      this.invalidateSubtree();
  }

  public setDomOriginOffset( x: number, y: number ): void
  {
    this.dom_origin_x = x;
    this.dom_origin_y = y;
    this.dom_last_translate = "";
    this.dom_last_transform = "";
  }

  public isDomMounted(): boolean
  {
    return this.dom_host !== null && this.dom_node !== null && this.dom_node.parentNode === this.dom_host;
  }

  protected setDomBackdrop( css: string ): void
  {
    if ( css === this.dom_last_backdrop )
      return;
    this.dom_last_backdrop = css;

    if ( !css )
    {
      this.dom_backdrop?.remove();
      this.dom_backdrop = null;
      return;
    }

    if ( !this.dom_backdrop )
    {
      const backdrop = document.createElement( "div" );
      backdrop.className = "ui-dom-backdrop";
      const s = backdrop.style;
      s.position = "absolute";
      s.left = "0";
      s.top = "0";
      s.width = "100%";
      s.height = "100%";
      s.pointerEvents = "none";
      this.dom_backdrop = backdrop;
      this.dom_host?.insertBefore( backdrop, this.dom_host.firstChild );
    }
    this.dom_backdrop.style.background = css;
  }

  protected syncDomContent( _node: HTMLElement ): boolean
  {
    return true;
  }

  protected isDomNative(): boolean
  {
    return this.dom_mode && this.dom_last_native;
  }

  private startRenderDom(): void
  {
    const node = this.getDomNode();
    const native = this.syncDomContent( node );
    this.dom_last_native = native;

    const w = this.rect.getW();
    const h = this.rect.getH();
    const rect_x = this.rect.getX();
    const rect_y = this.rect.getY();
    const box = `${rect_x}|${rect_y}|${w}|${h}`;
    if ( box !== this.dom_last_size )
    {
      this.dom_last_size = box;
      node.style.left = `${snapToDevicePx( rect_x )}px`;
      node.style.top = `${snapToDevicePx( rect_y )}px`;
      node.style.width = `${snapToDevicePx( w )}px`;
      node.style.height = `${snapToDevicePx( h )}px`;
    }

    this.applyDomTransformStyle( node, native );

    if ( native )
    {
      if ( this.dom_island )
      {
        this.dom_island.remove();
        this.dom_island = null;
        this.dom_island_ctx = null;
      }
      return;
    }

    this.dom_null_ctx = this.ctx;
    this.ctx = this.ensureDomIsland( node, w, h );
  }

  private applyDomTransformStyle( node: HTMLElement, native: boolean ): void
  {
    const pos = this.pixelPosForRender();
    const scale = this.getScaleForRender();
    const collapsed = this.isDomScaleCollapsedStatic();
    const scaled = ( scale.x !== 1 || scale.y !== 1 ) && !collapsed;
    const rotation = this.rotatable ? this.getRotationForRender() : 0;

    const static_transform = native ? this.domStaticTransform() : "";

    if ( DOM_INDIVIDUAL_TRANSFORMS )
    {
      const translate = `${snapToDevicePx( pos.x + this.dom_origin_x )}px ${snapToDevicePx( pos.y + this.dom_origin_y )}px`;
      if ( translate !== this.dom_last_translate ) { this.dom_last_translate = translate; node.style.translate = translate; }
      const rotate = rotation !== 0 ? `${rotation}rad` : "";
      if ( rotate !== this.dom_last_rotate ) { this.dom_last_rotate = rotate; node.style.rotate = rotate; }
      const scale_css = scaled ? domScaleCss( scale.x, scale.y ) : "";
      if ( scale_css !== this.dom_last_scale ) { this.dom_last_scale = scale_css; node.style.scale = scale_css; }
      if ( static_transform !== this.dom_last_static_transform ) { this.dom_last_static_transform = static_transform; node.style.transform = static_transform; }
    }
    else
    {
      let transform = `translate(${pos.x + this.dom_origin_x}px, ${pos.y + this.dom_origin_y}px)`;
      if ( rotation !== 0 ) transform += ` rotate(${rotation}rad)`;
      if ( scaled ) transform += ` scale(${domScaleCss( scale.x, scale.y ).replace( " ", ", " )})`;
      if ( static_transform ) transform += ` ${static_transform}`;
      if ( transform !== this.dom_last_transform ) { this.dom_last_transform = transform; node.style.transform = transform; }
    }

    if ( collapsed )
      this.syncDomVisibility();

    const opacity = this.getOpacityForRender() * ( native ? this.image_opacity : 1 );
    const opacity_css = opacity >= 1 ? "" : `${opacity}`;
    if ( opacity_css !== this.dom_last_opacity ) { this.dom_last_opacity = opacity_css; node.style.opacity = opacity_css; }
  }

  protected domStaticTransform(): string
  {
    let t = "";
    if ( this.direction_x !== 1 || this.direction_y !== 1 ) t += `scale(${this.direction_x}, ${this.direction_y})`;
    if ( this.rotation_angle !== 0 ) t += `${t ? " " : ""}rotate(${this.rotation_angle}rad)`;
    return t;
  }

  private ensureDomIsland( node: HTMLElement, w: number, h: number ): CanvasRenderingContext2D
  {
    const dpr = window.devicePixelRatio || 1;
    if ( !this.dom_island )
    {
      const canvas = document.createElement( "canvas" );
      canvas.className = "ui-dom-island";
      const s = canvas.style;
      s.position = "absolute";
      s.left = "0";
      s.top = "0";
      s.pointerEvents = "none";
      s.imageRendering = "auto";
      node.insertBefore( canvas, node.firstChild );
      this.dom_island = canvas;
      this.dom_island_ctx = canvas.getContext( "2d" ) as CanvasRenderingContext2D;
    }

    const canvas = this.dom_island;
    const pw = Math.max( 1, Math.ceil( w * dpr ) );
    const ph = Math.max( 1, Math.ceil( h * dpr ) );
    if ( canvas.width !== pw || canvas.height !== ph )
    {
      canvas.width = pw;
      canvas.height = ph;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    const ctx = this.dom_island_ctx as CanvasRenderingContext2D;
    ctx.setTransform( dpr, 0, 0, dpr, ( w / 2 ) * dpr, ( h / 2 ) * dpr );
    ctx.clearRect( -w / 2, -h / 2, w, h );
    ctx.save();
    return ctx;
  }

  private finishRenderDom(): void
  {
    if ( this.dom_null_ctx )
    {
      this.ctx.restore();
      this.ctx = this.dom_null_ctx;
      this.dom_null_ctx = null;
    }
    this.renderChildrenDom();
  }

  private renderChildrenDom(): void
  {
    for ( let i = 0; i < this.children.length; i++ )
    {
      const child = this.children[i];
      child.syncDomVisibility();
      if ( child.shouldBeRendered() )
        child.render();
    }
  }

  private isDomScaleCollapsedStatic(): boolean
  {
    const scale = this.getScaleForRender();
    return isDomScaleCollapsed( scale.x, scale.y ) && !this.isDomScaleDriven();
  }

  private domVisibilityCss(): string
  {
    const transparent = this.getOpacityForRender() <= 0.001 && !this.isDomOpacityDriven();
    return transparent || this.isDomScaleCollapsedStatic() ? "hidden" : "";
  }

  private domVisibilityChanged(): boolean
  {
    return this.domVisibilityCss() !== this.dom_last_visibility;
  }

  private syncDomVisibility(): void
  {
    const visibility = this.domVisibilityCss();
    if ( visibility !== this.dom_last_visibility )
    {
      this.dom_last_visibility = visibility;
      this.getDomNode().style.visibility = visibility;
    }
  }

  private static readonly DOM_ANIMATION_DEFER_MS = 32;

  protected override onTransformAdded( transform: TransformBase ): void
  {
    if ( !this.dom_mode || !DOM_ANIMATIONS_SUPPORTED )
      return;
    this.scheduleDomAnimation( transform );
  }

  protected override onTransformRescheduled( transform: TransformBase ): void
  {
    if ( !this.dom_mode || !DOM_ANIMATIONS_SUPPORTED )
      return;
    if ( transform.dom_animation )
    {
      if ( transform.dom_animation.playState !== "finished" )
        return;
      releaseDomAnimation( transform, false );
    }
    this.scheduleDomAnimation( transform );
  }

  private domTranslateOrigin(): DomTranslateOrigin
  {
    return { x: this.dom_origin_x, y: this.dom_origin_y };
  }

  private scheduleDomAnimation( transform: TransformBase ): void
  {
    if ( transform.dom_pending_timer !== null )
    {
      clearTimeout( transform.dom_pending_timer );
      transform.dom_pending_timer = null;
    }

    const now = performance.now();
    const delay = transform.startTime() - now;
    const predecessor = transform.isImportant() ? this.domImportantPredecessor( transform ) : null;
    const chained = predecessor !== null && domPredecessorDoneBy( predecessor, transform.startTime() );
    if ( delay <= UIElement.DOM_ANIMATION_DEFER_MS || chained )
    {
      transform.dom_animation = startDomAnimation( this.getDomNode(), transform, now, this.domTranslateOrigin() );
      this.pauseDomAnimationIfHidden( transform.dom_animation );
      return;
    }

    transform.dom_pending_timer = setTimeout( () =>
    {
      transform.dom_pending_timer = null;
      transform.dom_animation = startDomAnimation( this.getDomNode(), transform, performance.now(), this.domTranslateOrigin() );
      this.pauseDomAnimationIfHidden( transform.dom_animation );
    }, delay );
  }

  private pauseDomAnimationIfHidden( anim: Animation | null ): void
  {
    if ( !anim )
      return;
    const root = this.getRootElement();
    if ( root.dom_hidden )
    {
      anim.pause();
      return;
    }
    const host = root.dom_host;
    if ( host && host.style.visibility === "hidden" )
      pauseForHiddenHost( anim );
  }

  protected override onTransformRemoved( transform: TransformBase, finished: boolean ): void
  {
    if ( transform.dom_pending_timer !== null )
    {
      clearTimeout( transform.dom_pending_timer );
      transform.dom_pending_timer = null;
    }
    if ( !transform.dom_animation )
      return;

    if ( finished )
      this.applyDomTransformStyle( this.getDomNode(), this.dom_last_native );
    releaseDomAnimation( transform, finished && transform.isImportant() );
  }

  public hideElement( duration: number = 240, easing: Easing = Easing.EaseOutQuad ): void
  {
    this.fadeOut( duration, undefined, easing );
  }

  public showElement( duration: number = 240, easing: Easing = Easing.EaseInQuad ): void
  {
    this.fadeIn( duration, undefined, easing );
  }

  public forceRender(): void
  {
    this.setShouldBeRendered();
    this.render();
  }

  public forceRedraw(): void
  {
    this.setShouldBeRendered();
  }

  // Alias used by canvas-detached elements (e.g. the leaderboard floating card)
  // that render themselves outside the normal tree walk.
  public requestRender(): void
  {
    this.setShouldBeRendered();
  }

  // Repaints this element and its immediate parent — used when an async asset
  // (e.g. a Facebook avatar photo) arrives after a one-shot render of a scroll
  // row and needs to replace the placeholder in place.
  public forceRenderParent(): void
  {
    this.forceMakeShouldBeRendered();
    if ( this.parent_element )
    {
      this.parent_element.forceMakeShouldBeRendered();
      this.parent_element.render();
    }
    else
    {
      this.render();
    }
  }

  public invalidateSubtree(): void
  {
    this.setShouldBeRendered();

    for ( const child of this.children )
      child.invalidateSubtree();
  }

  public skipRenderSubtree(): void
  {
    this.resetShouldBeRendered();

    for ( const child of this.children )
      child.skipRenderSubtree();
  }

  public hideAndShow( hide_duration: number = 240, show_duration: number = 240, delay: number = 0 ): void
  {
    this.fadeOutAndIn( hide_duration, show_duration, delay );
  }

  public blinkElement( blink_duration: number = 120, blink_count: number = 3 ): void
  {
    const now = performance.now();
    for ( let i = 0; i < blink_count; i++ )
    {
      const fadeOutStart = now + ( i * blink_duration * 2 );
      const fadeInStart = fadeOutStart + blink_duration;

      this.fadeOut( blink_duration, fadeOutStart );
      this.fadeIn( blink_duration, fadeInStart );
    }
  }

  public setOnClick( onClick: ( p: Vector2 ) => void, replay_on_root = false )
  {
    this.onClick = onClick;
    this.replay_click_on_root = replay_on_root;
  }

  public getOnClick(): ( ( p: Vector2 ) => void ) | undefined
  {
    return this.onClick;
  }

  /** Diagnostic snapshot of the hit-test branches at a canvas-local point. */
  public debugTouchHitPath( p: Vector2 ): Record<string, unknown>[]
  {
    const hits: Record<string, unknown>[] = [];
    const visit = ( element: UIElement, parent_point: Vector2, depth: number ): void =>
    {
      const local = element.toLocal( parent_point );
      if ( !element.containsPointInner( local ) )
        return;

      const pos = element.pixelPosForRender();
      hits.push( {
        depth,
        name: element.name(),
        position: { x: pos.x, y: pos.y },
        size: { width: element.rect.getW(), height: element.rect.getH() },
        local_point: { x: local.x, y: local.y },
        clickable: element.is_clickable,
        button: element.is_button,
        has_on_click: element.onClick !== undefined,
        rendered: element.shouldBeRendered(),
      } );

      for ( let i = element.children.length - 1; i >= 0; i-- )
      {
        const child = element.children[i];
        if ( !child.isPartOfParent_notSeparateElement() )
          visit( child, local, depth + 1 );
      }
    };

    visit( this, p, 0 );
    return hits;
  }

  public static setOnAnyTouchCallback( callback: ( element: UIElement ) => void ): void
  {
    UIElement.onAnyTouchCallback = callback;
  }

  public cleanup(): void
  {
    this.children.forEach( child => child.cleanup() );

    if ( this.dom_mode )
    {
      this.releaseDomAnimations( false );
      this.dom_node?.remove();
      this.dom_backdrop?.remove();
      this.dom_host = null;
    }
  }

  // Called when the owning dialog starts closing; DOM-backed elements
  // (UITextInput) override it to drop focus and hide their HTML overlay.
  public releaseFocusRecursive(): void
  {
    this.children.forEach( child => child.releaseFocusRecursive() );
  }

  protected updateBasic( now: number ): void
  {
    //console.log( `UIElement.updateBasic()   name ${this.name()}   this.shouldBeRendered() ${this.shouldBeRendered()}` );

    this.updateSize( now );

    // Plain loops on purpose: this walks the whole element tree every frame,
    // and a per-call closure here was a steady source of GC pressure.
    let any_child_dirty = false;
    for ( let i = 0; i < this.children.length; i++ )
    {
      const child = this.children[i];
      if ( child.isPartOfParent_notSeparateElement() )
      {
        if ( this.dom_mode && ( child.shouldBeRendered() || child.domVisibilityChanged() ) )
          this.markDirtyFromChild();
        continue;
      }

      child.updateBasic( now );

      if ( child.shouldBeRendered() )
      {
        any_child_dirty = true;
        if ( this.dom_mode )
          this.markDirtyFromChild();
        else
          this.setShouldBeRendered();
      }
      else if ( this.dom_mode && child.domVisibilityChanged() )
      {
        this.markDirtyFromChild();
      }
    }

    this.onChildrenUpdated( any_child_dirty );

    if ( this.shouldBeRendered() && ( !this.dom_mode || this.isExplicitlyDirty() ) )
    {
      for ( let i = 0; i < this.children.length; i++ )
        this.children[i].invalidateSubtree();
    }
  }

  // Runs after the children updated and before a dirty element invalidates its
  // whole subtree. any_child_dirty tells a real change below apart from this
  // element only moving (see ScrollContentContainer).
  protected onChildrenUpdated( _any_child_dirty: boolean ): void { }

  public getRootElement(): UIElement
  {
    let current: UIElement = this;
    while ( current.parent_element )
      current = current.parent_element;
    return current;
  }

  public forceMakeShouldBeRendered()
  {
    if ( this.dom_mode )
    {
      this.invalidateSubtree();
      return;
    }
    this.getRootElement().invalidateSubtree();
  }

  public abstract render(): void;

  public abstract name(): string;

  public renderChildren(): void
  {
    if ( this.dom_mode )
    {
      this.renderChildrenDom();
      return;
    }

    const w2 = this.rect.getW2();
    const h2 = this.rect.getH2();

    this.ctx.translate( -w2, -h2 );

    this.children.forEach( child =>
    {
      child.render();
    } );
  }

  protected renderChildren_forMostTopParent(): void
  {
    this.resetShouldBeRendered();

    if ( this.dom_mode )
    {
      this.startRenderDom();
      this.finishRender();
      return;
    }

    const pixel_pos = this.pixelPosForRender();

    this.ctx.save();
    this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );

    this.finishRender();
  }

  protected startRender(): void
  {
    this.resetShouldBeRendered();

    if ( this.dom_mode )
    {
      this.startRenderDom();
      return;
    }

    const pixel_pos = this.pixelPosForRender();
    const scale     = this.getScaleForRender();
    const opacity   = this.getOpacityForRender();

    this.ctx.save();

    if ( this.rotatable )
    {
      const cx = this.rect.getW() / 2;
      const cy = this.rect.getH() / 2;
      this.ctx.translate( pixel_pos.x + cx, pixel_pos.y + cy );

      const angle = this.getRotationForRender();
      if ( angle !== 0 ) this.ctx.rotate( angle );

      // this.ctx.translate( -cx , -cy );
    }
    else
    {
      this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );
    }

    this.ctx.scale( scale.x, scale.y );
    this.ctx.globalAlpha *= opacity;
  }

  // protected startRender(): void
  // {
  //   this.resetShouldBeRendered();

  //   const pixel_pos = this.pixelPosForRender();
  //   const scale = this.getScaleForRender();
  //   const opacity = this.getOpacityForRender();

  //   this.ctx.save();
  //   this.ctx.translate(
  //     pixel_pos.x + this.rect.centerX(),
  //     pixel_pos.y + this.rect.centerY()
  //   );

  //   if ( this.rotatable )
  //   {
  //     const angle = this.getRotationForRender();
  //     if ( angle !== 0 ) this.ctx.rotate( angle );
  //   }

  //   // this.ctx.translate( pixel_pos.x + this.rect.centerX(), pixel_pos.y + this.rect.centerY() );
  //   this.ctx.scale( scale.x, scale.y );
  //   this.ctx.globalAlpha *= opacity;
  // }

  protected finishRender(): void
  {
    if ( this.dom_mode )
    {
      this.finishRenderDom();
      return;
    }

    this.renderChildren();
    this.ctx.restore();
  }

  public setContextRecursive( ctx: CanvasRenderingContext2D ): void
  {
    if ( this.dom_mode )
    {
      if ( !dom_set_context_warned )
      {
        dom_set_context_warned = true;
        console.warn( `[UIElement] setContextRecursive ignored on DOM backend (${this.name()})` );
      }
      return;
    }

    this.ctx = ctx;
    this.children.forEach( child => child.setContextRecursive( ctx ) );
  }

  protected isPartOfParent_notSeparateElement() { return this.is_part_of_parent_not_a_separate_element; }

  addChild( elem: UIElement, is_part_of_parent_not_a_separate_element: boolean = false ): void
  {
    if ( is_part_of_parent_not_a_separate_element )
      elem.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;

    elem.setParentElement( this );
    this.children.push( elem );

    if ( this.dom_mode )
      this.getDomNode().appendChild( elem.getDomNode() );
  }

  addChildAtIndex( elem: UIElement, index: number, is_part_of_parent_not_a_separate_element: boolean = false ): void
  {
    if ( is_part_of_parent_not_a_separate_element )
      elem.is_part_of_parent_not_a_separate_element = is_part_of_parent_not_a_separate_element;

    elem.setParentElement( this );
    this.children.splice( index, 0, elem );

    if ( this.dom_mode )
    {
      const next = this.children[index + 1];
      this.getDomNode().insertBefore( elem.getDomNode(), next ? next.getDomNode() : null );
    }
  }

  childIndexOf( elem: UIElement ): number
  {
    return this.children.indexOf( elem );
  }

  removeChild( elem: UIElement ): void
  {
    const index = this.children.indexOf( elem );
    if ( index > -1 )
    {
      elem.setParentElement( null );
      this.children.splice( index, 1 );
      if ( this.dom_mode )
      {
        elem.releaseDomAnimations( true );
        elem.dom_node?.remove();
      }
    }
  }

  protected containsPointInner( p: Vector2 ): boolean
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    //console.log( `containsPointInner( p ${p.x},${p.y} )   w ${w}  h ${h}   name ${this.name()}` );

    return p.x >= 0
      && p.y >= 0
      && p.x <= w
      && p.y <= h;
  }

  protected toLocal( p: Vector2 ): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2( p.x - pixel_pos.x, p.y - pixel_pos.y );
    //return new Vector2( p.x - this.pixel_pos.x - this.rect.getX(), p.y - this.pixel_pos.y - this.rect.getY() );
  }

  // Рекурсивная обработка клика/тача (событие пробегается по дереву)
  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( !this.is_clickable )
      return false;

    /*
        const dbg_text = document.getElementById( 'dbgText' );
        if ( dbg_text )
          dbg_text.innerText = `onTouchStart(${p.toString()})`;
    */
    const local_p: Vector2 = this.toLocal( p );
    //console.log( `UIElement.onTouchStart( p ${p.toString()})   local_p ${local_p.toString()}   rect ${this.rect.toStr()}   name ${this.name()}` );
    if ( !this.containsPointInner( local_p ) )
      return false;

    if ( UIElement.onAnyTouchCallback )
    {
      UIElement.onAnyTouchCallback( this );
    }

    //console.log( `UIElement.onTouchStart()   name ${this.name()}   children.length ${this.children.length}    is_part_of_parent_not_a_separate_element ${this.is_part_of_parent_not_a_separate_element}` );
    // Сначала проверяем дочерние элементы (обратный порядок – верхние слои первыми)
    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      if ( child.isPartOfParent_notSeparateElement() )
        continue;

      if ( child.onTouchStart( local_p, now ) )
        return true;
    }

    // ни один из детей не обработал событие
    //console.log( `this.is_button ${this.is_button}   name ${this.name()}` );
    if ( this.is_button )
    {
      this.pressButton( now );
    }

    this.was_pressed_inside = true;

    return true;
  }

  public getChildren(): readonly UIElement[]
  {
    return this.children;
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    /*
        const dbg_text = document.getElementById( 'dbgText' );
        if ( dbg_text )
          dbg_text.innerText = `onTouchEnd(${p.toString()})`;
    */
    const local_p: Vector2 = this.toLocal( p );
    //if ( !this.containsPoint( local_p ) )
    {
      if ( this.is_button )
        this.releaseButton( now );
      //return false;
    }

    // Сначала проверяем дочерние элементы (обратный порядок – верхние слои первыми)
    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      if ( !child )
        continue;
      child.onTouchEnd( local_p, now, p_global ? p_global : p );
    }
    //console.log(`p_global ${p_global}   p ${p}   local_p ${local_p}   name ${this.name()}` );

    // ни один из детей не обработал событие.
    //this.releaseButton( now );
    if ( this.was_pressed_inside && this.containsPointInner( local_p ) && this.onClick )
    {
      const click_point = p_global ? p_global : p;
      const replay_root = this.replay_click_on_root ? this.getRootElement() : null;
      this.onClick( click_point );

      // Dismiss layers are removed by onClick. Replay after the current event
      // finishes so the now-exposed element receives a complete down/up tap.
      if ( replay_root && replay_root !== this )
      {
        queueMicrotask( () =>
        {
          const replay_now = performance.now();
          replay_root.onTouchStart( click_point, replay_now );
          replay_root.onTouchEnd( click_point, replay_now );
        } );
      }
    }

    this.was_pressed_inside = false;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    const local_p: Vector2 = this.toLocal( p );
    if ( !this.containsPointInner( local_p ) )
    {
      if ( this.is_pressed )
        this.releaseButton( now );
      return;
    }

    for ( let i = this.children.length - 1; i >= 0; i-- )
    {
      const child = this.children[i];
      child.onTouchMove( local_p, now );
    }
  }

  /**
   * Cancels an active press without dispatching onClick. Scroll containers use
   * this when a tap turns into a drag, so nested buttons return from their
   * pressed bump state while the scrolling gesture keeps ownership.
   */
  public cancelTouch( now: number ): void
  {
    if ( this.is_pressed )
      this.releaseButton( now );

    this.was_pressed_inside = false;

    for ( const child of this.children )
      child.cancelTouch( now );
  }

  private pressButton( now: number ): void
  {
    this.is_pressed = true;
    if (this.shouldAnimatePress()) {
      this.animatePress( now );
    }
  }

  protected shouldAnimatePress(): boolean
  {
    return true;
  }
  private releaseButton( now: number ): void
  {
    if ( !this.is_pressed )
      return;

    this.is_pressed = false;
    if (this.shouldAnimatePress()) {
      this.animateRelease( now );
    }
  }

  private updateSize( now: number )
  {
    //if ( !this.isAnimating() )
    //  return;

    //if ( now < this.anim_start_time )
    //  return;

    this.updateAllTransforms( now );
    this.clearFinishedTransforms_andCallOnFinished( now );
  }

  // Простейшая анимация «вдавливания» (уменьшение/увеличение)
  // Press feedback is a two-step snap (down / up), not a tween: every tween
  // frame repaints the whole host canvas, so a single tap used to cost ~15
  // full-canvas rasters — the fps dip felt on every button press.
  private animatePress( now: number ): void
  {
    this.setScaleImmediate( new Vector2( this.pressed_scale, this.pressed_scale ) );
    // Keep the bump, but redraw through the root so scroll-container clipping
    // and transforms are applied. Drawing the immediate parent directly can
    // leak scrolled content into the safe-area strip.
    this.forceMakeShouldBeRendered();
  }
  private animateRelease( now: number ): void
  {
    this.setScaleImmediate( new Vector2( 1, 1 ) );
    this.forceMakeShouldBeRendered();
  }
  public getRect(): Rect
  {
    return this.rect;
  }

  protected drawRectAreaForTest(): void
  {
    this.ctx.beginPath();
    this.ctx.fillStyle = ColorScheme.black_transparent;
    this.ctx.rect( -this.rect.getW2(), -this.rect.getH2(), this.rect.getW(), this.rect.getH() );
    this.ctx.fill();
  }
}
